import type { FetchError } from 'ofetch';
import type { _GettersTree } from 'pinia';

import { defineStore } from 'pinia';

import type { Address, ShippingMethod } from '~/stores/address/types';

import { ApiMethod, handleApiError } from '~/utils/apiClient';
import { ValidationState } from '~/utils/common';
import { StorageKey } from '~/utils/storage';

const STORE_ID = 'address';

interface Actions {
  fetchShippingMethods: (locale: Locale) => Promise<ShippingMethod[] | null>;
}

interface State {
  currentBillingAddress: Address;
  currentBillingAddressState: ValidationState;
  currentShippingAddress: Address;
  currentShippingAddressState: ValidationState;
  currentShippingMethod: ShippingMethod | null;
  shippingMethods: ShippingMethod[] | null;
}

export const useAddressStore = defineStore<typeof STORE_ID, State, _GettersTree<State>, Actions>(STORE_ID, {
  actions: {
    async fetchShippingMethods(locale: Locale) {
      const currentCartId = getItemStorage(StorageKey.CURRENT_CART);

      try {
        const response = await this.$api.fetch<ShippingMethod[]>(
          ApiMethod.GET,
          '/shipping_methods/matching',
          {
            cart_id: currentCartId ?? undefined,
            locale,
          },
          true,
        );

        if (response.data) {
          this.shippingMethods = response.data;
          return response.data;
        }
      } catch (error) {
        const fetchError = error as FetchError;
        await handleApiError(fetchError);

        return null;
      }

      return null;
    },
  },

  state: () => ({
    currentBillingAddress: { country: DEFAULT_COUNTRY },
    currentBillingAddressState: ValidationState.INITIAL,
    currentShippingAddress: { country: DEFAULT_COUNTRY },
    currentShippingAddressState: ValidationState.INITIAL,
    currentShippingMethod: null,
    shippingMethods: null,
  }),
});
